<script lang="ts" setup>
import type { FormInstance, FormSubmitEvent } from '@primevue/forms'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import * as yup from 'yup'

const emits = defineEmits<{
  submit: [event: FormSubmitEvent]
}>()
const form = ref<FormInstance>()
const formFields = ref<Record<string, any>>({})
const props = withDefaults(
  defineProps<{
    disabled?: boolean
    fields: FormField[]
    loading: boolean
    submitProps?: FormSubmitProps
  }>(),
  {
    disabled: false,
    loading: false,
  }
)
const schemas = ref<Record<string, yup.Schema>>({})

const resolver = computed(() => yupResolver(yup.object().shape(schemas.value)))

const addField = (name: string, schema: yup.Schema) => {
  schema && (schemas.value[name] = schema)
}

const addFormField = (name: string, element?: Element) => {
  if (!element) return
  formFields.value[name] = element
}

const getField = (name: string) => formFields.value[name]

const setFieldValue = (name: string, value: any) => {
  if (!form.value) return
  form.value.setFieldValue(name, value)
}

provide('$fcDynamicForm', { addField, addFormField })
defineExpose({ getField, setFieldValue })
</script>

<template>
  <Form
    v-slot="$form"
    :resolver
    class="space-y-5"
    ref="form"
    @submit="(event) => emits('submit', event)"
  >
    <slot name="before" />

    <slot v-bind="$form">
      <template
        v-for="{ as, class_, name, label, listeners, ...rest } in props.fields"
        :key="name"
      >
        <DynamicFormField
          v-if="['Checkbox', 'ToggleSwitch'].includes(as)"
          :checkbox="['Checkbox', 'ToggleSwitch'].includes(as)"
          :name="name"
        >
          <div class="flex gap-2 items-center">
            <DynamicFormControl
              v-bind="rest"
              v-on="listeners ? { ...listeners } : {}"
              :as
              :disabled="props.disabled || props.loading"
            />
            <DynamicFormLabel>{{ label }}</DynamicFormLabel>
          </div>
          <DynamicFormMessage />
        </DynamicFormField>

        <DynamicFormField v-else :name="name">
          <DynamicFormLabel>{{ label }}</DynamicFormLabel>
          <DynamicFormControl
            v-bind="rest"
            v-on="listeners ? { ...listeners } : {}"
            :as
            :disabled="props.disabled || props.loading"
          />
          <DynamicFormMessage />
        </DynamicFormField>
      </template>

      <slot name="after-fields"></slot>

      <DynamicFormSubmit
        :disabled="props.disabled || props.loading"
        :loading="props.loading"
        v-bind="props.submitProps"
      />
    </slot>

    <slot name="after" />
  </Form>
</template>
